/**
 * Commercial Admin Portal - Main App Component
 */

import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout, Spin } from 'antd'
import Dashboard from './pages/Dashboard'
import BusinessComparison from './pages/BusinessComparison'
import Reports from './pages/Reports'
import { LandingPage } from './pages/LandingPage'
import AppHeader from './components/AppHeader'
import AppSider from './components/AppSider'

const { Content } = Layout

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    // Function to check authentication
    const checkAuth = () => {
      // First, check if token is passed in URL
      const urlParams = new URLSearchParams(window.location.search)
      const urlToken = urlParams.get('token')
      
      if (urlToken) {
        // Store token from URL to localStorage
        localStorage.setItem('token', urlToken)
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname)
      }

      const token = localStorage.getItem('token')
      const user = localStorage.getItem('user')
      
      if (token && user) {
        try {
          JSON.parse(user)
          // Check if user is a corporate user
          setIsAuthenticated(true)
        } catch (error) {
          console.error('Failed to parse user data:', error)
          setIsAuthenticated(false)
        }
      } else if (token) {
        // We have a token but no user data - still allow access
        // The API calls will use the token
        setIsAuthenticated(true)
      } else {
        setIsAuthenticated(false)
      }
    }

    // Check auth on mount
    checkAuth()

    // Listen for storage events (when user logs in from another tab/app)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user') {
        checkAuth()
      }
    }

    // Add event listener
    window.addEventListener('storage', handleStorageChange)

    // Also poll every 2 seconds to check for auth changes (same tab)
    const interval = setInterval(checkAuth, 2000)

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Loading..." />
      </div>
    )
  }

  // Show landing page if not authenticated
  if (!isAuthenticated) {
    return (
      <LandingPage 
        onGetStarted={() => window.location.href = 'https://serviceflow-pro.com'}
      />
    )
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader />
      <Layout>
        <AppSider />
        <Layout style={{ padding: '24px' }}>
          <Content
            style={{
              padding: 24,
              margin: 0,
              minHeight: 280,
              background: '#fff',
              borderRadius: 8,
            }}
          >
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/comparison" element={<BusinessComparison />} />
              <Route path="/reports" element={<Reports />} />
            </Routes>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  )
}

export default App

